import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, AlertCircle, Trash2, X, AlertTriangle, FileText, Settings, Truck, CreditCard, Share2, ToggleRight, ShieldCheck, ExternalLink } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

import { API_BASE, apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface SettingItem {
  value: string;
  description: string;
}

// T&C settings
interface TCItem {
  key: string;
  label: string;
  placeholder: string;
}

const TC_SECTIONS: TCItem[] = [
  { key: 'terms_of_service', label: '服務條款', placeholder: '請輸入服務條款內容...' },
  { key: 'privacy_policy', label: '隱私政策', placeholder: '請輸入隱私政策內容...' },
  { key: 'refund_policy', label: '退換貨政策', placeholder: '請輸入退換貨政策內容...' },
  { key: 'delivery_policy', label: '送貨政策', placeholder: '請輸入送貨政策內容...' },
];

interface KpayInfo {
  env: string;
  baseUrl: string;
  mid: string | null;
  hasPrivateKey: boolean;
  hasPlatformPublicKey: boolean;
  isConfigured: boolean;
  provider: string;
  txnStats: {
    paymentCount: number;
    refundCount: number;
    lastTxnAt: number | null;
  };
  reference: {
    prodMid: string;
    uatMid: string;
    portalUrl: string;
    docsUrl: string;
    moduleDoc: string;
    webhookUrl: string;
    returnUrlPattern: string;
    ipWhitelistNote: string;
  };
}

type TabKey = 'basic' | 'payment' | 'order' | 'social' | 'features' | 'terms';

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  keys: string[];
}

const TAB_GROUPS: TabDef[] = [
  {
    key: 'basic',
    label: '基本資訊',
    icon: <Settings size={18} />,
    keys: ['site_name', 'business_phone', 'admin_phone', 'company_name'],
  },
  {
    key: 'payment',
    label: '付款與銀行',
    icon: <CreditCard size={18} />,
    keys: ['bank_account', 'fps_id', 'payment.provider'],
  },
  {
    key: 'order',
    label: '訂單與送貨',
    icon: <Truck size={18} />,
    keys: ['min_days_advance', 'order_cutoff_time', 'delivery_time_note', 'delivery_fee', 'master_delivery_min_order_amount', 'pickup_location', 'pickup_time_note', 'pickup_min_order_amount'],
  },
  {
    key: 'social',
    label: '社交與推薦',
    icon: <Share2 size={18} />,
    keys: ['social.community_link', 'social.facebook_page_url', 'social.instagram_url', 'referral.share_message_template', 'referral.commission_percentage', 'referral.max_commission_per_order'],
  },
  {
    key: 'features',
    label: '功能開關',
    icon: <ToggleRight size={18} />,
    keys: ['feature.first_order_discount_enabled', 'feature.repurchase_voucher_enabled', 'feature.referral_voucher_enabled', 'feature.welcome_gift_enabled', 'feature.egg_campaign_enabled', 'feature.milestone_gift_enabled', 'feature.ai_translate_enabled'],
  },
  {
    key: 'terms',
    label: '條款與細則',
    icon: <FileText size={18} />,
    keys: [], // handled separately
  },
];

const defaultSettings: Record<string, SettingItem> = {
  site_name: { value: '好餸社企', description: '網站名稱' },
  business_phone: { value: '85251164453', description: 'WhatsApp 業務電話' },
  admin_phone: { value: '85251164453', description: '管理員通知電話' },
  bank_account: { value: 'DBS A/C - 016-000227829', description: '銀行帳號' },
  fps_id: { value: 'FPS - 108810334', description: 'FPS 轉數快 ID' },
  company_name: { value: 'DELICIOUS EXPRESS LTD', description: '公司名稱' },
  min_days_advance: { value: '2', description: '最少提前預訂天數' },
  order_cutoff_time: { value: '20:00', description: '每日截單時間（HH:MM，截單前落單最早聽日送，截單後順延一日）' },
  delivery_time_note: { value: '每日下午 4:00 前', description: '送貨時間提示文字（顯示於落單頁送貨日期下方）' },
  pickup_location: { value: '', description: '門市自取地點（全域預設，留空則使用各屋苑設定）' },
  pickup_time_note: { value: '每日 15:00-19:00', description: '自取時段提示文字（顯示於落單頁）' },
  pickup_min_order_amount: { value: '50', description: '自取最低消費（HKD，0 = 不設最低消費）' },
  master_delivery_min_order_amount: { value: '350', description: '無鄰里店（無 gs code）送貨單最低消費（HKD，0 = 不設最低消費）' },
  'payment.provider': { value: 'bank_transfer', description: '付款供應商（bank_transfer / stripe / payme / fps_qr / aggregated）' },
  'social.community_link': { value: 'https://chat.whatsapp.com/F24dPerpH5tCmcXRBvTA2E', description: 'WhatsApp 社群邀請連結' },
  'social.facebook_page_url': { value: 'https://www.facebook.com/deliciousexpresshk', description: 'Facebook 專頁連結' },
  'social.instagram_url': { value: '', description: 'Instagram 連結（留空則不顯示）' },
  'referral.share_message_template': { value: '剛在好餸社企買到靚餸，每張訂單都支持 SEN 青年就業！用我這條連結落單，一起支持社企：', description: '推薦分享預設文案' },
  delivery_fee: { value: '50', description: '未達最低消費時自動加收運費（HKD，0 = 免運）。適用於一般送貨單及鄰里店送貨單，後者同時記入「鄰里店運費收入」' },
  'referral.commission_percentage': { value: '', description: '推薦佣金百分比（例：10 = 10%，留空則手動設定）' },
  'referral.max_commission_per_order': { value: '', description: '每筆訂單佣金上限（HKD，留空則無上限）' },
  'feature.first_order_discount_enabled': { value: '1', description: '首次網購折扣（滿$100減$20 / 經推薦碼減$30）' },
  'feature.repurchase_voucher_enabled': { value: '1', description: '復購回贈券（滿$150送$30，滿$100可用）' },
  'feature.referral_voucher_enabled': { value: '1', description: '推薦獎賞券（推薦人與被推薦人各$30）' },
  'feature.welcome_gift_enabled': { value: '1', description: '新客首單迎新禮（新鮮蔬菜一包）' },
  'feature.egg_campaign_enabled': { value: '1', description: '雞蛋活動回贈券（WhatsApp關鍵字觸發）' },
  'feature.milestone_gift_enabled': { value: '1', description: '推薦里程碑禮品（銅/銀/金章）' },
  'feature.ai_translate_enabled': { value: '1', description: 'AI 產品翻譯（產品只輸入中文時自動翻譯回填英文名/描述；需已設定 AI_TRANSLATE_* secrets）' },
};

// Default T&C content
const defaultTC: Record<string, string> = {
  terms_of_service: '',
  privacy_policy: '',
  refund_policy: '',
  delivery_policy: '',
};

type DangerTarget = 'orders' | 'customers' | 'referrals';

interface DangerActionDef {
  key: DangerTarget;
  label: string;
  description: string;
  confirmPhrase: string;
  endpoint: string;
}

const DANGER_ACTIONS: DangerActionDef[] = [
  {
    key: 'orders',
    label: '清除所有訂單',
    description: '永久刪除 order_records 內所有訂單記錄，無法復原。',
    confirmPhrase: 'DELETE_ORDERS',
    endpoint: '/api/admin/data/orders',
  },
  {
    key: 'customers',
    label: '清除所有客戶資料',
    description: '永久刪除 member_profiles 內所有客戶資料，無法復原。',
    confirmPhrase: 'DELETE_CUSTOMERS',
    endpoint: '/api/admin/data/customers',
  },
  {
    key: 'referrals',
    label: '清除所有推薦資料',
    description: '永久刪除 referral_profiles、referral_dashboards 及 referral_bindings 內所有推薦資料，無法復原。',
    confirmPhrase: 'DELETE_REFERRALS',
    endpoint: '/api/admin/data/referrals',
  },
];

// Clear product cache button component
function ClearCacheButton() {
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const handleClear = async () => {
    if (!confirm('確定要清除產品緩存嗎？清除後前端會即時重新載入最新產品列表。')) return;
    setClearing(true);
    setResult(null);
    try {
      const res = await apiFetch('/api/public/admin/clear-product-cache', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setResult('✅ 緩存已清除');
      } else {
        setResult('❌ ' + (data.error || '清除失敗'));
      }
    } catch {
      setResult('❌ 網絡錯誤');
    } finally {
      setClearing(false);
      setTimeout(() => setResult(null), 5000);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleClear}
        disabled={clearing}
        className="flex items-center px-4 py-3 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 disabled:opacity-60 font-medium text-sm dark:bg-orange-900/30 dark:text-orange-300 dark:hover:bg-orange-900/50"
      >
        <Trash2 className="w-4 h-4 mr-1" />
        {clearing ? '清除中...' : '清除產品緩存'}
      </button>
      {result && <span className="text-sm">{result}</span>}
    </div>
  );
}

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<Record<string, SettingItem>>(defaultSettings);
  const [tcContent, setTcContent] = useState<Record<string, string>>(defaultTC);
  const [activeTab, setActiveTab] = useState<TabKey>('basic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('admin_token');
  const { can } = usePermissions();

  const [currentRole, setCurrentRole] = useState<string>('');
  const [dangerAction, setDangerAction] = useState<DangerActionDef | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [dangerLoading, setDangerLoading] = useState(false);
  const [kpayInfo, setKpayInfo] = useState<KpayInfo | null>(null);
  const [kpayInfoError, setKpayInfoError] = useState('');

  const canUseDangerZone = currentRole === 'super_admin';

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    const storedUser = localStorage.getItem('admin_user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setCurrentRole(parsed.role || '');
      } catch {
        setCurrentRole('');
      }
    }
    fetchSettings();
  }, [token, navigate]);

  // KPay 商戶資訊（super_admin 專屬，只讀）
  useEffect(() => {
    if (!token || currentRole !== 'super_admin') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/public/admin/kpay-info');
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setKpayInfo(data);
        } else if (!cancelled) {
          setKpayInfoError('無法載入 KPay 商戶資訊');
        }
      } catch {
        if (!cancelled) setKpayInfoError('網絡錯誤，無法載入 KPay 商戶資訊');
      }
    })();
    return () => { cancelled = true; };
  }, [token, currentRole]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const merged = { ...defaultSettings };
        const mergedTC = { ...defaultTC };
        for (const [key, item] of Object.entries(data)) {
          const raw = item as any;
          const itemValue = raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw;
          const itemDescription = raw && typeof raw === 'object' && 'description' in raw ? raw.description : '';
          if (merged[key]) {
            merged[key] = { ...merged[key], value: itemValue };
          } else if (key in mergedTC) {
            mergedTC[key] = itemValue || '';
          } else {
            merged[key] = { value: itemValue, description: itemDescription || '' };
          }
        }
        setSettings(merged);
        setTcContent(mergedTC);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取設置失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload: Record<string, any> = {};
      for (const [key, item] of Object.entries(settings)) {
        payload[key] = { value: item.value, description: item.description };
      }
      // Include T&C content
      for (const [key, value] of Object.entries(tcContent)) {
        if (value.trim()) {
          payload[key] = { value, description: TC_SECTIONS.find(s => s.key === key)?.label || key };
        }
      }

      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSuccess('設置已保存！');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('保存失敗');
      }
    } catch (e) {
      setError('保存失敗');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));
  };

  const updateTC = (key: string, value: string) => {
    setTcContent(prev => ({ ...prev, [key]: value }));
  };

  const openDangerAction = (action: DangerActionDef) => {
    if (!canUseDangerZone) return;
    setDangerAction(action);
    setConfirmText('');
    setError('');
  };

  const closeDangerAction = () => {
    setDangerAction(null);
    setConfirmText('');
  };

  const executeDangerAction = async () => {
    if (!dangerAction) return;
    if (confirmText !== dangerAction.confirmPhrase) {
      setError(`確認碼不正確，請輸入「${dangerAction.confirmPhrase}」`);
      return;
    }
    setDangerLoading(true);
    setError('');
    try {
      const res = await apiFetch(dangerAction.endpoint, { method: 'DELETE' });
      if (res.ok) {
        setSuccess(`已執行：${dangerAction.label}`);
        closeDangerAction();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `執行 ${dangerAction.label} 失敗`);
      }
    } catch {
      setError('網絡錯誤，操作未成功');
    } finally {
      setDangerLoading(false);
    }
  };

  const renderSettingField = (key: string, item: SettingItem) => {
    const isFeature = key.startsWith('feature.');
    return (
      <div key={key}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {item.description}
          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-mono">({key})</span>
        </label>
        {isFeature ? (
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={String(item.value).trim() === '1'}
              onChange={e => updateSetting(key, e.target.checked ? '1' : '0')}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
              {String(item.value).trim() === '1' ? '已啟用' : '已停用'}
            </span>
          </label>
        ) : (
          <input
            value={item.value}
            onChange={e => updateSetting(key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-gray-800 dark:text-white"
          />
        )}
      </div>
    );
  };

  const activeGroup = TAB_GROUPS.find(t => t.key === activeTab);
  const visibleSettings = activeGroup && activeGroup.keys.length > 0
    ? activeGroup.keys.filter(k => settings[k])
    : [];

  return (
    <AdminLayout currentPage="settings">
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <AlertCircle size={20} />
            {error}
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">✕</button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">加載中...</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            {/* TAB Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex overflow-x-auto">
                {TAB_GROUPS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                      activeTab === tab.key
                        ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'terms' ? (
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">條款與細則管理</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    以下內容將顯示於前台對應頁面。支援純文字，可使用換行分隔段落。
                  </p>
                  {TC_SECTIONS.map(tc => (
                    <div key={tc.key}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {tc.label}
                      </label>
                      <textarea
                        value={tcContent[tc.key] || ''}
                        onChange={e => updateTC(tc.key, e.target.value)}
                        placeholder={tc.placeholder}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-gray-800 dark:text-white resize-y"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {visibleSettings.map(key => renderSettingField(key, settings[key]))}
                  </div>

                  {/* KPay 商戶資訊（super_admin 專屬） */}
                  {activeTab === 'payment' && currentRole === 'super_admin' && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-stone-50 dark:bg-gray-900/40">
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck size={20} className="text-orange-600 dark:text-orange-400" />
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">KPay 商戶資訊</h3>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          kpayInfo?.env === 'prod'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        }`}>
                          {kpayInfo ? (kpayInfo.env === 'prod' ? 'PROD 正式環境' : 'UAT 測試環境') : '—'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        唯讀資訊，供營運管理與對賬用；密鑰內容存於 Cloudflare Secrets，永不在此顯示。詳見 docs/modules/kpay.md。
                      </p>

                      {kpayInfoError && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm rounded-lg mb-4">
                          {kpayInfoError}
                        </div>
                      )}

                      {kpayInfo && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">商戶號 MID（當前 Secrets）</div>
                              <div className="font-mono text-gray-900 dark:text-white break-all">{kpayInfo.mid || '（未配置）'}</div>
                              {kpayInfo.mid && (
                                <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                                  {kpayInfo.mid === kpayInfo.reference.prodMid ? '✓ 與正式環境 MID 一致' : kpayInfo.mid === kpayInfo.reference.uatMid ? '⚠ 仍是 UAT MID' : '⚠ 與已知 MID 不符，請核對'}
                                </div>
                              )}
                            </div>
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">API 請求地址（KPAY_BASE_URL）</div>
                              <div className="font-mono text-sm text-gray-900 dark:text-white break-all">{kpayInfo.baseUrl}</div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Credentials 狀態</div>
                              <div className="text-sm text-gray-900 dark:text-white">
                                商戶私鑰 {kpayInfo.hasPrivateKey ? <span className="text-green-600 dark:text-green-400">✓ 已配置</span> : <span className="text-red-600 dark:text-red-400">✗ 未配置</span>}
                                <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
                                平台公鑰 {kpayInfo.hasPlatformPublicKey ? <span className="text-green-600 dark:text-green-400">✓ 已配置</span> : <span className="text-red-600 dark:text-red-400">✗ 未配置</span>}
                              </div>
                              <div className="text-sm mt-1 text-gray-900 dark:text-white">
                                顧客結帳啟用（payment.provider）：
                                {kpayInfo.provider === 'kpay'
                                  ? <span className="text-green-600 dark:text-green-400 font-medium">kpay（線上收款已啟用）</span>
                                  : <span className="text-gray-500 dark:text-gray-400">{kpayInfo.provider}（KPay 未啟用，維持手動流程）</span>}
                              </div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">KPay 交易概況</div>
                              <div className="text-sm text-gray-900 dark:text-white">
                                付款 {kpayInfo.txnStats.paymentCount} 筆 · 退款 {kpayInfo.txnStats.refundCount} 筆
                              </div>
                              <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                                最後交易：{kpayInfo.txnStats.lastTxnAt ? new Date(kpayInfo.txnStats.lastTxnAt * 1000).toLocaleString('zh-HK') : '—'}
                              </div>
                            </div>
                          </div>

                          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">回調與跳轉地址（正式域名）</div>
                            <div className="font-mono text-xs text-gray-900 dark:text-white break-all space-y-1">
                              <div>notifyUrl: {kpayInfo.reference.webhookUrl}</div>
                              <div>returnUrl: {kpayInfo.reference.returnUrlPattern}</div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 text-sm">
                            <a href={kpayInfo.reference.portalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-xs font-medium">
                              <ExternalLink size={14} /> KPay 商戶後台（下載密鑰）
                            </a>
                            <a href={kpayInfo.reference.docsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium">
                              <ExternalLink size={14} /> KPay API 文檔
                            </a>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              參考 MID — 正式：{kpayInfo.reference.prodMid} · UAT：{kpayInfo.reference.uatMid}
                            </span>
                          </div>

                          {kpayInfo.env === 'prod' && (
                            <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs rounded-lg">
                              ⚠ {kpayInfo.reference.ipWhitelistNote}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {can(PERMISSIONS.SETTINGS_WRITE) && (
                <div className="mt-8 flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
                  <ClearCacheButton />
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 font-medium"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {saving ? '保存中...' : '保存設置'}
                  </button>
                </div>
              )}

              {/* Danger zone */}
              <div className="mt-8 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  <h3 className="text-lg font-bold text-red-800 dark:text-red-200">危險刪除區</h3>
                </div>
                <p className="text-sm text-red-700 dark:text-red-300 mb-4">
                  以下操作會永久刪除資料，無法復原。僅供管理員使用。
                </p>

                {!canUseDangerZone && (
                  <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm rounded-lg">
                    你目前的權限無法使用危險刪除功能。
                  </div>
                )}

                <div className="space-y-3">
                  {DANGER_ACTIONS.map((action) => (
                    <div
                      key={action.key}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-lg p-4"
                    >
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{action.label}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{action.description}</div>
                      </div>
                      <button
                        onClick={() => openDangerAction(action)}
                        disabled={!canUseDangerZone}
                        className="inline-flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        <Trash2 size={16} /> 刪除
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Danger confirmation modal */}
      {dangerAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertTriangle size={22} />
                <h3 className="text-lg font-bold">確認{dangerAction.label}</h3>
              </div>
              <button onClick={closeDangerAction} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={22} />
              </button>
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              你即將{dangerAction.label}。此操作不可復原，請在下方輸入確認碼：
            </p>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 mb-4 text-center font-mono text-gray-900 dark:text-white select-all">
              {dangerAction.confirmPhrase}
            </div>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`輸入「${dangerAction.confirmPhrase}」以確認`}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none dark:bg-gray-800 dark:text-white mb-6"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={closeDangerAction}
                disabled={dangerLoading}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
              >
                取消
              </button>
              <button
                onClick={executeDangerAction}
                disabled={dangerLoading || confirmText !== dangerAction.confirmPhrase}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {dangerLoading ? '執行中...' : <><Trash2 size={16} /> 確認刪除</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSettings;
